// Smooth multi-stop color gradient used to color countries on both maps.
//
// Spec requirement: color is NOT bucketed into a fixed number of classes -
// it is a continuous interpolation across the gradient stops below, driven
// by each country's click count relative to the currently most-clicked
// country (a dynamic upper bound, recomputed from live data - see
// computeRatio). Pure, dependency-free and unit-tested (see
// src/lib/__tests__ / the project root's manual test scripts) because a
// subtly wrong interpolation is a purely visual bug that is easy to miss by
// eye but easy to get exactly right with a few numeric assertions.

export const DEFAULT_GRADIENT_STOPS = [
  "#EEF5FA",
  "#CFE8FF",
  "#9ED8FF",
  "#68C5E8",
  "#62D49A",
  "#FFE66D",
  "#FFBE45",
  "#FF8A3D",
  "#EF4D4D",
  "#8B1010",
] as const;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function hexToRgb(hex: string): Rgb {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex);
  if (!match) throw new Error(`invalid hex color: ${JSON.stringify(hex)}`);
  const int = parseInt(match[1], 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

export function rgbToCss({ r, g, b }: Rgb): string {
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Interpolates a color across N gradient stops for t in [0, 1]. t=0 is the
 * first stop, t=1 is the last stop, and values in between blend smoothly
 * across whichever pair of adjacent stops they fall into.
 */
export function interpolateGradient(stops: readonly string[], t: number): Rgb {
  if (stops.length === 0) throw new Error("stops must not be empty");
  const clamped = Math.min(1, Math.max(0, t));
  if (stops.length === 1) return hexToRgb(stops[0]);

  const scaled = clamped * (stops.length - 1);
  const index = Math.min(stops.length - 2, Math.floor(scaled));
  const localT = scaled - index;

  const from = hexToRgb(stops[index]);
  const to = hexToRgb(stops[index + 1]);
  return {
    r: lerp(from.r, to.r, localT),
    g: lerp(from.g, to.g, localT),
    b: lerp(from.b, to.b, localT),
  };
}

/**
 * ratio = countryClicks / maxClicks, using 0 when there is no data yet
 * (maxClicks <= 0) so callers do not need a special case.
 */
export function computeRatio(countryClicks: number, maxClicks: number): number {
  if (!Number.isFinite(countryClicks) || countryClicks <= 0) return 0;
  if (!Number.isFinite(maxClicks) || maxClicks <= 0) return 0;
  return Math.min(1, Math.max(0, countryClicks / maxClicks));
}

/**
 * Applies the gamma adjustment from the spec (adjustedRatio = ratio^0.65)
 * so low-click countries remain visually distinguishable from each other
 * instead of all bunching up near the "no clicks" end of the gradient.
 */
export function adjustRatio(ratio: number, gamma = 0.65): number {
  const clamped = Math.min(1, Math.max(0, ratio));
  return Math.pow(clamped, gamma);
}

export interface ColorForClicksOptions {
  gradientStops?: readonly string[];
  gamma?: number;
}

/**
 * Same computation as colorForClicks, but returns the intermediate Rgb
 * value instead of a CSS string - used where a caller also needs the raw
 * channels (e.g. the sidebar list, which derives its text color from the
 * same background via relativeLuminance/textColorForBackground in
 * src/lib/luminance.ts).
 *
 * Per the spec, a country with zero clicks uses ratio 0, which lands
 * exactly on the first gradient stop ("zelo svetla modro-siva za države
 * brez klikov" - the lightest stop is defined to already mean "no clicks",
 * there is no separate/different "no data" color).
 */
export function rgbForClicks(
  clicks: number,
  maxClicks: number,
  options: ColorForClicksOptions = {},
): Rgb {
  const stops = options.gradientStops ?? DEFAULT_GRADIENT_STOPS;
  const ratio = computeRatio(clicks, maxClicks);
  const adjusted = adjustRatio(ratio, options.gamma ?? 0.65);
  return interpolateGradient(stops, adjusted);
}

/**
 * The single function the map calls: given a country's click count and the
 * current maximum across all countries, returns the exact CSS color to
 * paint it.
 */
export function colorForClicks(
  clicks: number,
  maxClicks: number,
  options: ColorForClicksOptions = {},
): string {
  return rgbToCss(rgbForClicks(clicks, maxClicks, options));
}
